import { throttle } from 'lodash-es';
import { useEffect, useRef, RefObject } from 'react';
import { logError } from 'utils/helpers';

const THROTTLE = 200;

/**
 * attaches and removes throttled scroll event listener to execute a callback once
 * the element associated with the ref parameter has come 1/7 of the way into the page
 *
 * @param action Callback function to fire once the component is in view
 * @param ref an existing ref. Default provided as ref(null)
 * @param offset vertical offset for when the action should fire.
 *               A larger offset will delay the action from firing
 *               until the component is further up the page. @default = 0
 *
 * @returns the Ref passed to the hook, or a new null ref.
 */
const useScrolledIntoViewOnce = (
  action: () => void,
  ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null),
  offset = 0,
): RefObject<HTMLDivElement> => {
  const handleScroll = () => {
    if (isInView(ref, offset)) {
      action();
      stopListening();
    }
  };

  const handleScrollThrottled = throttle(handleScroll, THROTTLE);

  const stopListening: () => void = () => {
    window.removeEventListener('scroll', handleScrollThrottled);
  };

  useEffect(() => {
    if (isInView(ref, offset)) {
      action();
      return;
    }
    window.addEventListener('scroll', handleScrollThrottled);
    return stopListening;
  }, []);

  return ref;
};

export const isInView = (
  ref: RefObject<HTMLDivElement>,
  offset: number,
  _window: Window = window,
): boolean => {
  if (!ref.current) {
    logError(
      `Can't determine if object is in view. Something is wrong with the ref argument.`,
      {
        component: 'hooks',
        function: 'useScrolledIntoViewOnce -> isInView',
      },
    );
    return false;
  }

  const windowHeight = _window.innerHeight;
  const { top: containerTop } = ref.current.getBoundingClientRect();

  // Distance above the bottom of the screen before component registers
  const deadZone = windowHeight / 7;
  // Component distance above the bottom of the screen.
  const topPosition = windowHeight - containerTop;

  // Is the component ABOVE the deadzone?
  return topPosition + offset > deadZone;
};

export default useScrolledIntoViewOnce;
